import { useDeferredValue, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FileText, Printer, RotateCcw } from 'lucide-react';
import { InvoiceStatus, VerifiedOrderPaymentStatus, type InvoiceListItem } from '@optiflow/types';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { PageHeader } from '@/components/ui/PageHeader';
import { Pagination } from '@/components/ui/Pagination';
import { Select } from '@/components/ui/Select';
import { useBranch } from '@/hooks/useBranch';
import { useCurrency } from '@/hooks/useCurrency';
import { useLocale } from '@/hooks/useLocale';
import { apiErrorMessage } from '@/services/apiClient';
import { formatDate } from '@/utils/format';
import { useInvoicesQuery } from '../api/sales.api';
import { InvoiceStatusBadge, OrderPaymentBadge } from '../components/InvoiceBadges';
import { printInvoice } from '../invoicePrint';

const PAGE_SIZE = 20;

/**
 * Every invoice issued in the branches the viewer can reach.
 *
 * The page finds, reads and reprints invoices; it never creates or voids one, because issuing belongs to
 * the sale and there is no void workflow. The payment column is the order's status from the canonical
 * receivable, labelled as the order's, since an invoice has no settlement of its own.
 */
export function InvoicesPage() {
  const { t } = useTranslation();
  const { branches } = useBranch();
  const { format } = useCurrency();
  const { locale } = useLocale();

  const [search, setSearch] = useState('');
  const [branchId, setBranchId] = useState('');
  const [status, setStatus] = useState<InvoiceStatus | ''>('');
  const [paymentStatus, setPaymentStatus] = useState<VerifiedOrderPaymentStatus | ''>('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);

  const deferredSearch = useDeferredValue(search);
  const { data, isPending, isError, error, refetch, isFetching } = useInvoicesQuery({
    page,
    pageSize: PAGE_SIZE,
    search: deferredSearch,
    branchId,
    status,
    paymentStatus,
    dateFrom,
    dateTo,
  });

  const filtered = Boolean(search || branchId || status || paymentStatus || dateFrom || dateTo);

  const onFilter =
    <T,>(set: (value: T) => void) =>
    (value: T) => {
      set(value);
      setPage(1);
    };

  const resetFilters = () => {
    setSearch('');
    setBranchId('');
    setStatus('');
    setPaymentStatus('');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  const columns: Column<InvoiceListItem>[] = [
    {
      key: 'reference',
      header: t('sales.invoices.fields.reference'),
      primary: true,
      render: (invoice) => (
        <span className="text-ink font-mono font-medium" dir="ltr">
          {invoice.reference}
        </span>
      ),
    },
    {
      key: 'order',
      header: t('sales.invoices.fields.order'),
      render: (invoice) => (
        <span className="text-muted font-mono text-sm" dir="ltr">
          {invoice.orderReference}
        </span>
      ),
    },
    {
      key: 'customer',
      header: t('sales.invoices.fields.customer'),
      render: (invoice) => (
        <span className="text-ink text-sm" dir="auto">
          {invoice.customerNameSnapshot ?? t('sales.invoices.walkIn')}
        </span>
      ),
    },
    {
      key: 'branch',
      header: t('sales.invoices.fields.branch'),
      render: (invoice) => (
        <span className="text-muted text-sm" dir="auto">
          {invoice.branchName}
        </span>
      ),
    },
    {
      key: 'issuedOn',
      header: t('sales.invoices.fields.issuedOn'),
      render: (invoice) => (
        <span className="text-muted text-sm">{formatDate(invoice.issuedAt, locale)}</span>
      ),
    },
    {
      key: 'total',
      header: t('sales.invoices.fields.total'),
      align: 'end',
      render: (invoice) => (
        <span
          className={
            invoice.status === InvoiceStatus.VOID
              ? 'text-muted line-through'
              : 'text-ink font-medium tabular-nums'
          }
          dir="ltr"
        >
          {format(invoice.totalMinor)}
        </span>
      ),
    },
    {
      key: 'orderPayment',
      header: t('sales.invoices.fields.orderPayment'),
      render: (invoice) => (
        <OrderPaymentBadge
          status={invoice.orderPaymentStatus}
          orderCancelled={invoice.orderCancelled}
        />
      ),
    },
    {
      key: 'status',
      header: t('sales.invoices.fields.status'),
      render: (invoice) => <InvoiceStatusBadge status={invoice.status} />,
    },
    {
      key: 'actions',
      header: t('common.actions'),
      align: 'end',
      hideOnCard: true,
      render: (invoice) => <InvoiceRowActions invoice={invoice} />,
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title={t('sales.invoices.title')} subtitle={t('sales.invoices.subtitle')} />

      <Card>
        <CardHeader
          title={t('sales.invoices.title')}
          subtitle={
            data ? t('sales.invoices.count', { count: data.pagination.totalItems }) : undefined
          }
          action={
            filtered ? (
              <Button variant="ghost" size="sm" onClick={resetFilters}>
                <RotateCcw className="size-4" aria-hidden />
                {t('sales.invoices.filters.reset')}
              </Button>
            ) : undefined
          }
        />

        <div className="border-line grid gap-3 border-b px-4 py-3 sm:grid-cols-2 lg:grid-cols-3">
          <Input
            type="search"
            value={search}
            onChange={(event) => onFilter(setSearch)(event.target.value)}
            placeholder={t('sales.invoices.searchPlaceholder')}
            aria-label={t('sales.invoices.searchLabel')}
          />
          <Select
            options={[
              { value: '', label: t('sales.invoices.filters.allBranches') },
              ...branches.map((branch) => ({ value: branch.id, label: branch.name })),
            ]}
            value={branchId}
            aria-label={t('sales.invoices.fields.branch')}
            onChange={(event) => onFilter(setBranchId)(event.target.value)}
          />
          <Select
            options={[
              { value: '', label: t('sales.invoices.filters.allStatuses') },
              ...Object.values(InvoiceStatus).map((value) => ({
                value,
                label: t(`sales.invoices.status.${value}`),
              })),
            ]}
            value={status}
            aria-label={t('sales.invoices.fields.status')}
            onChange={(event) => onFilter(setStatus)(event.target.value as InvoiceStatus | '')}
          />
          <Select
            options={[
              { value: '', label: t('sales.invoices.filters.allPayments') },
              ...Object.values(VerifiedOrderPaymentStatus).map((value) => ({
                value,
                label: t(`sales.verify.payment.${value}`),
              })),
            ]}
            value={paymentStatus}
            aria-label={t('sales.invoices.fields.orderPayment')}
            onChange={(event) =>
              onFilter(setPaymentStatus)(event.target.value as VerifiedOrderPaymentStatus | '')
            }
          />
          <Input
            type="date"
            value={dateFrom}
            onChange={(event) => onFilter(setDateFrom)(event.target.value)}
            aria-label={t('sales.invoices.filters.dateFrom')}
          />
          <Input
            type="date"
            value={dateTo}
            onChange={(event) => onFilter(setDateTo)(event.target.value)}
            aria-label={t('sales.invoices.filters.dateTo')}
          />
        </div>

        <DataTable
          caption={t('sales.invoices.tableCaption')}
          columns={columns}
          rows={data?.items}
          rowKey={(invoice) => invoice.id}
          isLoading={isPending}
          isError={isError}
          errorMessage={apiErrorMessage(error) ?? t('sales.invoices.loadError')}
          onRetry={() => void refetch()}
          emptyIcon={FileText}
          emptyTitle={filtered ? t('sales.invoices.emptyFiltered') : t('sales.invoices.empty')}
          emptyDescription={filtered ? undefined : t('sales.invoices.emptyHint')}
          emptyAction={
            filtered ? (
              <Button variant="secondary" size="sm" onClick={resetFilters}>
                <RotateCcw className="size-4" aria-hidden />
                {t('sales.invoices.filters.reset')}
              </Button>
            ) : undefined
          }
          cardActions={(invoice) => <InvoiceRowActions invoice={invoice} />}
        />

        {data ? (
          <Pagination pagination={data.pagination} onPageChange={setPage} isBusy={isFetching} />
        ) : null}
      </Card>
    </div>
  );
}

/** Print and view, on the table row and on the phone card alike. */
function InvoiceRowActions({ invoice }: { invoice: InvoiceListItem }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <div className="flex flex-wrap justify-end gap-1">
      <Button
        variant="ghost"
        size="sm"
        aria-label={t('sales.invoices.printNamed', { reference: invoice.reference })}
        onClick={() => printInvoice(invoice.id)}
      >
        <Printer className="size-4" aria-hidden />
        {t('sales.invoice.print')}
      </Button>
      <Button
        variant="ghost"
        size="sm"
        aria-label={t('sales.invoices.openNamed', { reference: invoice.reference })}
        onClick={() => void navigate(`/sales/invoices/${invoice.id}`)}
      >
        {t('sales.invoices.view')}
      </Button>
    </div>
  );
}
